import React, { useState, useMemo } from 'react';
import { addAmericanaGuest } from '../api/americanas';
import './AmericanaGuestForm.css';
import 'font-awesome/css/font-awesome.min.css';

export default function AmericanaGuestForm({ americanaId, onGuestAdded, capacity, confirmedCount }) {
    const [expanded, setExpanded] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        level: ''
    });

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const { nextStatus, remainingSlots, showCapacityInfo } = useMemo(() => {
        const numericCapacity = Number(capacity);
        const hasCapacity = Number.isFinite(numericCapacity) && numericCapacity > 0;

        if (!hasCapacity) {
            return { nextStatus: 'confirmed', remainingSlots: null, showCapacityInfo: false };
        }

        const remaining = Math.max(numericCapacity - (confirmedCount ?? 0), 0);
        return {
            nextStatus: remaining > 0 ? 'confirmed' : 'waitlist',
            remainingSlots: remaining,
            showCapacityInfo: true,
        };
    }, [capacity, confirmedCount]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setSuccess(null);

        if (!formData.firstName.trim() || !formData.lastName.trim()) {
            setError('Nombre y apellidos son obligatorios.');
            return;
        }

        setSaving(true);
        try {
            const apiResult = await addAmericanaGuest(americanaId, {
                ...formData,
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                phone: formData.phone.trim() || null,
                level: formData.level.trim() || null,
                status: nextStatus,
            });

            const result = apiResult?.registration ? apiResult : { registration: apiResult ?? {} };
            const r = result.registration || {};
            const normalizedStatus = String(r.status ?? nextStatus ?? 'confirmed').toLowerCase();

            const registration = {
                id: r.id ?? Date.now(),
                status: normalizedStatus,
                createdAt: r.createdAt ?? new Date().toISOString(),
                guestId: r.guestId ?? r.guest?.id ?? r.id,
                guestInfo: {
                    firstName: r.guest?.firstName ?? formData.firstName.trim(),
                    lastName:  r.guest?.lastName  ?? formData.lastName.trim(),
                    phone:     r.guest?.phone     ?? (formData.phone.trim() || null),
                    level:     r.guest?.level     ?? (formData.level.trim() || null),
                }
            };

            await onGuestAdded?.({ registration });

            setSuccess(normalizedStatus === 'waitlist'
                ? 'Invitada añadida a la Lista de Espera.'
                : 'Invitada añadida correctamente.');

            setFormData({ firstName: '', lastName: '', phone: '', level: '' });

            // Opcional: Cerrar el acordeón automáticamente tras éxito
            // setTimeout(() => setExpanded(false), 2000);

        } catch (e) {
            setError(e?.response?.data?.message || 'No se pudo añadir la invitada.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="guest-wrapper">
            <div className={`guest-card ${expanded ? 'is-expanded' : ''}`}>

                <div
                    className="guest-header"
                    onClick={() => setExpanded(!expanded)}
                    role="button"
                    tabIndex={0}
                >
                    <div className="header-content">
                        <div className="icon-box">
                            <i className={`fa ${expanded ? 'fa-user-plus' : 'fa-user-plus'}`}></i>
                        </div>
                        <div className="header-text">
                            <h4>{expanded ? 'Nueva Invitada' : 'Añadir Jugadora Invitada'}</h4>
                            <p>{expanded
                                ? 'Completa los datos de la jugadora'
                                : 'Registra una jugadora invitada'}
                            </p>
                        </div>
                    </div>
                    <div className="chevron">
                        <i className="fa fa-chevron-down"></i>
                    </div>
                </div>

                {expanded && (
                    <div className="guest-body">
                        {showCapacityInfo && (
                            <div className={`capacity-badge ${nextStatus === 'waitlist' ? 'waitlist' : 'info'}`}>
                                <i className={`fa ${nextStatus === 'waitlist' ? 'fa-clock-o' : 'fa-check-circle'}`}></i>
                                {nextStatus === 'waitlist'
                                    ? 'Cupo completo. Se añadirá a Lista de Espera.'
                                    : `Quedan ${remainingSlots} plaza${remainingSlots !== 1 ? 's' : ''} disponibles.`
                                }
                            </div>
                        )}

                        {error && <div className="feedback-msg error"><i className="fa fa-exclamation-circle"></i> {error}</div>}
                        {success && <div className="feedback-msg success"><i className="fa fa-check"></i> {success}</div>}

                        <form onSubmit={handleSubmit}>
                            <div className="form-grid">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        placeholder="Nombre"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        placeholder="Apellidos"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-grid">
                                <div className="form-group">
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="Teléfono (Opcional)"
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="level"
                                        value={formData.level}
                                        onChange={handleChange}
                                        placeholder="Nivel (Ej: 3.5)"
                                    />
                                </div>
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="btn-add-guest" disabled={saving}>
                                    {saving ? (
                                        <span><i className="fa fa-spinner fa-spin"></i> Guardando...</span>
                                    ) : (
                                        <span><i className="fa fa-plus"></i> Guardar Invitada</span>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}